import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import type { CalendarSettingsRow } from '@easyflow/shared';
import { Loader2, RefreshCw, Sparkles, Trophy, Medal, Award, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import apiClient from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { cn } from '@/lib/utils';
import { useSocketOptional } from '@/contexts/SocketContext';

type AttendanceState =
  | 'NOT_DUE'
  | 'DUE_SOON'
  | 'LATE'
  | 'ABSENT'
  | 'ACTIVE'
  | 'ON_BREAK'
  | 'EARLY_CLOCKOUT'
  | 'COMPLETED'
  | 'WALK_IN'
  | 'NO_SHIFT';

interface ShiftWindow {
  start_time: string;
  end_time: string;
  is_overnight: boolean;
}

interface AttendanceItem {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  snapshot: {
    state: AttendanceState;
    shift: ShiftWindow | null;
  };
}

interface MatrixAppointment {
  id: string;
  user_id: string;
  assigned_to_id?: string | null;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  customer_name?: string;
  type_color?: string;
}

interface CalendarUser {
  id: string;
  name: string;
  email: string;
}

type CalendarSettings = Pick<CalendarSettingsRow,
  'slot_duration' | 'working_hours_start' | 'working_hours_end'>;

interface AppointmentTypeOption {
  id: string;
  name: string;
  color?: string;
  is_active?: boolean;
}

interface EligibilityCandidate {
  user_id: string;
  eligible?: boolean;
  score?: number;
  reasons?: string[];
  exclusion_reason?: string | null;
  attendance_state?: AttendanceState;
  required_skills_satisfied?: boolean;
  missing_skills?: string[];
  skills?: { skill_id: string; name: string; level: string; rank: number }[];
}

interface RequiredSkillInfo {
  skill_id: string;
  name: string;
  min_level: string;
  min_rank: number;
}

interface SlotRanking {
  start: string;
  end: string;
  candidates: EligibilityCandidate[];
}

interface Props {
  canManage?: boolean;
  selectedDate: Date;
  settings: CalendarSettings | null;
  calendarUsers: CalendarUser[];
  selectedStoreId: string;
  selectedUserId: string;
  serviceTypes?: AppointmentTypeOption[];
  onCellClick: (userId: string, slotStart: Date, slotEnd: Date, serviceId?: string) => void;
  onAppointmentClick?: (appointmentId: string) => void;
  /** Task #526: invoked when the resource label is clicked to open
   *  the side drawer with skills/shifts/services details. */
  onResourceClick?: (userId: string, userName: string) => void;
  /** When provided, the header exposes a date navigator (prev/next/today/picker)
   *  that writes back into the shared Calendar page state. This makes the
   *  implicit link between the Planning tab and the Matrix tab explicit:
   *  the user can see AND change the date directly from the matrix header. */
  onDateChange?: (date: Date) => void;
}

const STATE_STYLES: Record<AttendanceState, { bg: string; label: string }> = {
  ACTIVE: { bg: 'bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50', label: 'Attivo' },
  WALK_IN: { bg: 'bg-teal-50 dark:bg-teal-900/30 hover:bg-teal-100 dark:hover:bg-teal-900/50', label: 'Walk-in' },
  ON_BREAK: { bg: 'bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50', label: 'In pausa' },
  LATE: { bg: 'bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50', label: 'In ritardo' },
  DUE_SOON: { bg: 'bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/50', label: 'Imminente' },
  NOT_DUE: { bg: 'bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60', label: 'Non ancora' },
  ABSENT: { bg: 'bg-rose-50 dark:bg-rose-900/30', label: 'Assente' },
  EARLY_CLOCKOUT: { bg: 'bg-purple-50 dark:bg-purple-900/30', label: 'Uscita anticipata' },
  COMPLETED: { bg: 'bg-gray-100 dark:bg-gray-800/60', label: 'Completato' },
  NO_SHIFT: { bg: 'bg-gray-50 dark:bg-gray-900/40', label: 'Senza turno' },
};

const STATE_ORDER: AttendanceState[] = [
  'ACTIVE', 'WALK_IN', 'ON_BREAK', 'LATE', 'DUE_SOON', 'NOT_DUE',
  'ABSENT', 'EARLY_CLOCKOUT', 'COMPLETED', 'NO_SHIFT',
];

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseHM(v: string | undefined | null): { h: number; m: number } | null {
  if (!v) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(v);
  if (!m) return null;
  return { h: parseInt(m[1]!, 10), m: parseInt(m[2]!, 10) };
}

function dateAtTime(base: Date, t: { h: number; m: number }): Date {
  const d = new Date(base);
  d.setHours(t.h, t.m, 0, 0);
  return d;
}

function shiftCovers(shift: ShiftWindow | null, slotStart: Date, slotEnd: Date, baseDate: Date): boolean {
  if (!shift) return false;
  const s = parseHM(shift.start_time);
  const e = parseHM(shift.end_time) || { h: 23, m: 59 };
  if (!s) return false;
  const sStart = dateAtTime(baseDate, s);
  const sEnd = dateAtTime(baseDate, e);
  if (shift.is_overnight && sEnd <= sStart) sEnd.setDate(sEnd.getDate() + 1);
  return slotStart >= sStart && slotEnd <= sEnd;
}

function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function BookingMatrixGrid({
  canManage = false,
  selectedDate,
  settings,
  calendarUsers,
  selectedStoreId,
  selectedUserId,
  serviceTypes,
  onCellClick,
  onAppointmentClick,
  onResourceClick,
  onDateChange,
}: Props) {
  const [attendance, setAttendance] = useState<AttendanceItem[]>([]);
  const [appointments, setAppointments] = useState<MatrixAppointment[]>([]);
  const [mode, setMode] = useState<{ effective: string; auto: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [eligibility, setEligibility] = useState<{
    slot_rankings: SlotRanking[];
    required_skills: RequiredSkillInfo[];
  } | null>(null);
  const [eligLoading, setEligLoading] = useState(false);

  const dateStr = toLocalDateStr(selectedDate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const storeQS = selectedStoreId ? `?store=${selectedStoreId}` : '';
      const [attRes, apRes, modeRes] = await Promise.all([
        apiClient.get<{ date: string; items: AttendanceItem[] }>(`/calendar/attendance-status?date=${dateStr}`),
        apiClient.get<MatrixAppointment[]>(`/calendar/appointments/date/${dateStr}${storeQS}`),
        apiClient.get<{ effective: string; auto: boolean }>(`/calendar/availability-mode`),
      ]);
      if (attRes.success && attRes.data) setAttendance(attRes.data.items || []);
      if (apRes.success) setAppointments(apRes.data || []);
      if (modeRes.success && modeRes.data) setMode(modeRes.data);
      // [DEBUG MATRIX] temporaneo — rimuovere dopo aver risolto il caso "matrice vuota"
      console.log('[DEBUG MATRIX] dateStr=', dateStr, 'storeQS=', storeQS, 'selectedUserId=', selectedUserId);
      console.log('[DEBUG MATRIX] calendarUsers=', calendarUsers.map(u => ({ id: u.id, name: u.name })));
      console.log('[DEBUG MATRIX] attendance items=', (attRes.data?.items || []).length);
      console.log('[DEBUG MATRIX] appointments returned=', (apRes.data || []).length, apRes.data);
    } catch (err) {
      console.error('Matrix load error', err);
    } finally {
      setLoading(false);
    }
  }, [dateStr, selectedStoreId, calendarUsers, selectedUserId]);

  useEffect(() => { load(); }, [load]);

  // Skill + per-slot eligibility for the selected service. The backend
  // resolver is the single source of truth: it ranks every active user
  // for each visible slot, returning numeric scores and human reasons.
  // We feed back exactly the same slot grid the matrix is rendering so
  // the suggested-resource highlight matches the resolver's verdict.
  useEffect(() => {
    if (!selectedServiceId) {
      setEligibility(null);
      setEligLoading(false);
      return;
    }
    const startHM = parseHM(settings?.working_hours_start) || { h: 8, m: 0 };
    const endHM = parseHM(settings?.working_hours_end) || { h: 20, m: 0 };
    const step = settings?.slot_duration || 30;
    const slotPayload: { start: string; end: string }[] = [];
    let cur = dateAtTime(selectedDate, startHM);
    const limit = dateAtTime(selectedDate, endHM);
    while (cur < limit) {
      const next = new Date(cur.getTime() + step * 60_000);
      slotPayload.push({ start: cur.toISOString(), end: next.toISOString() });
      cur = next;
    }
    if (slotPayload.length === 0) {
      setEligibility({ slot_rankings: [], required_skills: [] });
      setEligLoading(false);
      return;
    }
    let cancelled = false;
    setEligLoading(true);
    apiClient.post<{ slot_rankings: SlotRanking[]; required_skills: RequiredSkillInfo[] }>(
      '/calendar/eligible-resources/per-slot',
      {
        service_id: selectedServiceId,
        slots: slotPayload,
        store_id: selectedStoreId || null,
      }
    ).then(res => {
      if (cancelled) return;
      if (res.success && res.data) {
        setEligibility({
          slot_rankings: res.data.slot_rankings || [],
          required_skills: res.data.required_skills || [],
        });
      } else {
        setEligibility({ slot_rankings: [], required_skills: [] });
      }
    }).catch(err => {
      if (!cancelled) {
        console.error('Eligible resources per-slot load error', err);
        setEligibility({ slot_rankings: [], required_skills: [] });
      }
    }).finally(() => {
      if (!cancelled) setEligLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedServiceId, selectedStoreId, settings, selectedDate]);

  // Skill summary derived from the resolver's per-slot rankings. Skills
  // don't depend on the slot, so the first ranking is enough.
  const skillByUser = useMemo(() => {
    const m = new Map<string, { ok: boolean; missing: string[] }>();
    if (!eligibility || eligibility.slot_rankings.length === 0) return m;
    for (const c of eligibility.slot_rankings[0]!.candidates!) {
      m.set(c.user_id, {
        ok: c.required_skills_satisfied !== false,
        missing: c.missing_skills || [],
      });
    }
    return m;
  }, [eligibility]);

  const activeServices = useMemo(
    () => (serviceTypes || []).filter(t => t.id && t.is_active !== false),
    [serviceTypes]
  );

  // Live updates: refetch matrix when appointments or attendance change
  // elsewhere in the app. Debounced to coalesce bursts and avoid flicker.
  const socketCtx = useSocketOptional();
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    if (!socketCtx?.isConnected) return;
    const { on, off } = socketCtx;
    const scheduleReload = () => {
      if (reloadTimerRef.current) return;
      reloadTimerRef.current = setTimeout(() => {
        reloadTimerRef.current = null;
        loadRef.current();
      }, 400);
    };
    on('calendar:appointment_changed', scheduleReload);
    on('hr:attendance_changed', scheduleReload);
    return () => {
      off('calendar:appointment_changed', scheduleReload);
      off('hr:attendance_changed', scheduleReload);
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [socketCtx, socketCtx?.isConnected, socketCtx?.socketVersion]);

  const slots = useMemo(() => {
    const start = parseHM(settings?.working_hours_start) || { h: 8, m: 0 };
    const end = parseHM(settings?.working_hours_end) || { h: 20, m: 0 };
    const step = settings?.slot_duration || 30;
    const out: { start: Date; end: Date; label: string }[] = [];
    let cur = dateAtTime(selectedDate, start);
    const limit = dateAtTime(selectedDate, end);
    while (cur < limit) {
      const next = new Date(cur.getTime() + step * 60_000);
      out.push({ start: new Date(cur), end: next, label: fmtTime(cur) });
      cur = next;
    }
    return out;
  }, [settings, selectedDate]);

  const rows = useMemo(() => {
    const attMap = new Map(attendance.map(a => [a.user_id, a]));
    let users = calendarUsers
      .filter(u => u.id)
      .map(u => {
        const att = attMap.get(u.id);
        return {
          user_id: u.id,
          name: u.name || u.email,
          state: (att?.snapshot.state || 'NO_SHIFT'),
          shift: att?.snapshot.shift || null,
        };
      });
    if (selectedUserId && selectedUserId !== 'all') {
      users = users.filter(u => u.user_id === selectedUserId);
    }
    users.sort((a, b) => {
      const ai = STATE_ORDER.indexOf(a.state);
      const bi = STATE_ORDER.indexOf(b.state);
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
    return users;
  }, [attendance, calendarUsers, selectedUserId]);

  const apptIndex = useMemo(() => {
    const m = new Map<string, MatrixAppointment[]>();
    for (const a of appointments) {
      if (a.status === 'cancelled') continue;
      const uid = a.assigned_to_id || a.user_id;
      if (!uid) continue;
      const list = m.get(uid) || [];
      list.push(a);
      m.set(uid, list);
    }
    return m;
  }, [appointments]);

  function findApptForCell(uid: string, slotStart: Date, slotEnd: Date): MatrixAppointment | null {
    const list = apptIndex.get(uid);
    if (!list) return null;
    for (const a of list) {
      const aStart = new Date(a.start_time);
      const aEnd = new Date(a.end_time);
      if (aStart < slotEnd && aEnd > slotStart) return a;
    }
    return null;
  }

  const selectedService = useMemo(
    () => activeServices.find(s => s.id === selectedServiceId) || null,
    [activeServices, selectedServiceId]
  );

  // Map user_id → display name for popover labels. Falls back to email
  // and finally to the user_id when the user is not in the calendar list.
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of calendarUsers) {
      if (u.id) m.set(u.id, u.name || u.email || u.id);
    }
    return m;
  }, [calendarUsers]);

  // Best candidate per slot is taken straight from the resolver's
  // ranking for that slot (already sorted by eligibility then score).
  // We also keep the top 3 eligible candidates so the dispatcher can
  // compare alternatives via a popover on the suggestion icon.
  const bestPerSlot = useMemo(() => {
    const out = new Map<number, {
      userId: string;
      score: number;
      reasons: string[];
      topCandidates: EligibilityCandidate[];
    }>();
    if (!selectedServiceId || !eligibility) return out;
    const visibleUserIds = new Set(rows.map(r => r.user_id));
    eligibility.slot_rankings.forEach((ranking, i) => {
      const eligibleCands = ranking.candidates.filter(c => c.eligible);
      const top = eligibleCands.find(c => visibleUserIds.has(c.user_id));
      if (!top) return;
      out.set(i, {
        userId: top.user_id,
        score: top.score ?? 0,
        reasons: top.reasons || [],
        topCandidates: eligibleCands.slice(0, 3),
      });
    });
    return out;
  }, [selectedServiceId, eligibility, rows]);

  return (
    <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <CardTitle className="text-lg text-gray-900 dark:text-white whitespace-nowrap">
              Matrice prenotazioni
            </CardTitle>
            {onDateChange ? (
              <div
                className="flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1 py-0.5"
                aria-label="Navigazione data matrice (condivisa con Planning)"
                title="Questa data è condivisa con la tab Planning"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    const prev = new Date(selectedDate);
                    prev.setDate(prev.getDate() - 1);
                    onDateChange(prev);
                  }}
                  aria-label="Giorno precedente"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium px-1 text-gray-800 dark:text-gray-100 whitespace-nowrap">
                  {selectedDate.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    const next = new Date(selectedDate);
                    next.setDate(next.getDate() + 1);
                    onDateChange(next);
                  }}
                  aria-label="Giorno successivo"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <label className="inline-flex items-center cursor-pointer px-1" aria-label="Scegli data">
                  <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <input
                    type="date"
                    className="sr-only"
                    value={toLocalDateStr(selectedDate)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      const [y, m, d] = v.split('-').map(Number);
                      if (!y || !m || !d) return;
                      const nd = new Date(selectedDate);
                      nd.setFullYear(y, m - 1, d);
                      onDateChange(nd);
                    }}
                  />
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => onDateChange(new Date())}
                >
                  Oggi
                </Button>
              </div>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-300">
                · {selectedDate.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            )}
            <span
              className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300"
              title="La data è condivisa con la tab Planning: cambiandola qui si aggiorna anche lì."
            >
              sincr. con Planning
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeServices.length > 0 && (
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-600 dark:text-gray-300">Servizio:</label>
                <select
                  value={selectedServiceId}
                  onChange={(e) => setSelectedServiceId(e.target.value)}
                  className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                >
                  <option value="">Tutti (nessun filtro skill)</option>
                  {activeServices.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {eligLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
              </div>
            )}
            {mode && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                Modalità {mode.effective}{mode.auto ? ' · auto' : ''}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
          {STATE_ORDER.filter(s => s !== 'NO_SHIFT').map(s => (
            <span key={s} className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700', STATE_STYLES[s].bg)}>
              <span className="w-2 h-2 rounded-full bg-current opacity-70" />
              {STATE_STYLES[s].label}
            </span>
          ))}
        </div>
        {selectedService && eligibility && (
          <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-300 space-y-0.5">
            {eligibility.required_skills.length === 0 ? (
              <div>Il servizio <strong>{selectedService.name}</strong> non richiede skill specifiche.</div>
            ) : (
              <div>
                Skill richieste per <strong>{selectedService.name}</strong>:{' '}
                {eligibility.required_skills.map(s => `${s.name} (${s.min_level})`).join(', ')}
              </div>
            )}
            <div className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-300">
              <Sparkles className="w-3 h-3" />
              <span>Le celle evidenziate indicano la risorsa suggerita per ogni slot</span>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            Nessuna risorsa disponibile per i filtri correnti.
          </div>
        ) : (
          <div className="overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="border-collapse text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200 min-w-[180px]">
                    Risorsa
                  </th>
                  {slots.map((s, i) => (
                    <th key={i} className="border-l border-gray-200 dark:border-gray-700 px-1 py-2 text-center font-normal text-gray-500 dark:text-gray-400 min-w-[44px]">
                      {s.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const stateStyle = STATE_STYLES[row.state] || STATE_STYLES.NO_SHIFT;
                  const serviceFilterActive = !!selectedServiceId && !!eligibility;
                  const skill = serviceFilterActive ? skillByUser.get(row.user_id) : undefined;
                  // When a service filter is active and the user is not in the
                  // candidate set returned by the resolver (e.g. excluded by
                  // store scope), treat the row as ineligible to avoid
                  // false-positive clickable cells.
                  const skillIneligible = serviceFilterActive && (!skill || !skill.ok);
                  const skillTitle = skillIneligible
                    ? ` · skill mancanti: ${skill?.missing?.join(', ') || 'risorsa non idonea'}`
                    : '';
                  return (
                    <tr
                      key={row.user_id}
                      className={cn(
                        'border-t border-gray-200 dark:border-gray-700',
                        skillIneligible && 'opacity-50'
                      )}
                    >
                      <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 px-3 py-2">
                        {onResourceClick ? (
                          <button
                            type="button"
                            onClick={() => onResourceClick(row.user_id, row.name)}
                            className="text-left w-full hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded"
                            title="Apri dettagli risorsa (skill, turni, servizi)"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.name}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              {stateStyle.label}
                              {skillIneligible && (
                                <span className="ml-1 text-rose-600 dark:text-rose-400">
                                  · skill mancanti
                                </span>
                              )}
                            </div>
                          </button>
                        ) : (
                          <>
                            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{row.name}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              {stateStyle.label}
                              {skillIneligible && (
                                <span className="ml-1 text-rose-600 dark:text-rose-400">
                                  · skill mancanti
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                      {slots.map((slot, i) => {
                        const appt = findApptForCell(row.user_id, slot.start, slot.end);
                        const inShift = shiftCovers(row.shift, slot.start, slot.end, selectedDate);
                        if (appt) {
                          const aStart = new Date(appt.start_time);
                          const isFirstCell = aStart >= slot.start && aStart < slot.end;
                          return (
                            <td
                              key={i}
                              onClick={() => onAppointmentClick?.(appt.id)}
                              className="border-l border-gray-200 dark:border-gray-700 p-0 cursor-pointer"
                              title={`${appt.title}${appt.customer_name ? ' — ' + appt.customer_name : ''}`}
                            >
                              <div
                                className="h-9 w-full flex items-center px-1 text-white text-[10px] truncate"
                                style={{ backgroundColor: appt.type_color || '#6366f1' }}
                              >
                                {isFirstCell ? (appt.customer_name || appt.title) : ''}
                              </div>
                            </td>
                          );
                        }
                        const stateBlocked = ['ABSENT', 'EARLY_CLOCKOUT', 'COMPLETED'].includes(row.state);
                        const blocked = stateBlocked || skillIneligible;
                        const cellEligible = !selectedServiceId || (!skillIneligible && inShift);
                        const cellBg = !cellEligible
                          ? 'bg-gray-100 dark:bg-gray-900/40'
                          : inShift
                            ? stateStyle.bg
                            : (row.state === 'NO_SHIFT' ? STATE_STYLES.NO_SHIFT.bg : 'bg-gray-50 dark:bg-gray-900/30');
                        const best = bestPerSlot.get(i);
                        const isBest = !!best && best.userId === row.user_id;
                        const bestTitle = isBest
                          ? ` · ⭐ migliore per questo slot · score ${best.score}${best.reasons.length ? ` · ${best.reasons.join(', ')}` : ''}`
                          : '';
                        return (
                          <td
                            key={i}
                            className={cn(
                              'border-l border-gray-200 dark:border-gray-700 h-9 transition-colors relative',
                              cellBg,
                              isBest && 'ring-2 ring-inset ring-indigo-400 dark:ring-indigo-300',
                              blocked
                                ? 'cursor-not-allowed opacity-60'
                                : selectedServiceId && !inShift
                                  ? 'cursor-pointer opacity-70'
                                  : 'cursor-pointer'
                            )}
                            onClick={() => {
                              if (!canManage || blocked) return;
                              onCellClick(row.user_id, slot.start, slot.end, selectedServiceId || undefined);
                            }}
                            title={`${row.name} · ${slot.label} · ${stateStyle.label}${inShift ? '' : ' (fuori turno)'}${skillTitle}${bestTitle}`}
                          >
                            {isBest && best && (
                              <TopCandidatesPopover
                                slotLabel={slot.label}
                                candidates={best.topCandidates}
                                userNameById={userNameById}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TopCandidatesPopoverProps {
  slotLabel: string;
  candidates: EligibilityCandidate[];
  userNameById: Map<string, string>;
}

// Small controlled popover so it opens both on hover (with a tiny delay
// to avoid accidental triggers) and on click/keyboard focus. Click on
// the trigger does not bubble up to the cell's click handler.
function TopCandidatesPopover({ slotLabel, candidates, userNameById }: TopCandidatesPopoverProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => cancelClose(), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          onMouseEnter={() => { cancelClose(); setOpen(true); }}
          onMouseLeave={scheduleClose}
          onFocus={() => { cancelClose(); setOpen(true); }}
          onBlur={scheduleClose}
          className="absolute top-0.5 right-0.5 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          aria-label="Mostra top 3 candidati per questo slot"
          title="Top 3 candidati per questo slot"
        >
          <Sparkles className="w-3 h-3 text-indigo-500 dark:text-indigo-300" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-3"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Top candidati · {slotLabel}
        </div>
        {candidates.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Nessuna alternativa idonea per questo slot.
          </div>
        ) : (
          <ol className="space-y-2">
            {candidates.map((c, idx) => {
              const RankIcon = idx === 0 ? Trophy : idx === 1 ? Medal : Award;
              const rankColor = idx === 0
                ? 'text-amber-500'
                : idx === 1
                  ? 'text-slate-400'
                  : 'text-orange-700 dark:text-orange-400';
              const stateLabel = c.attendance_state
                ? STATE_STYLES[c.attendance_state]?.label || c.attendance_state
                : '—';
              return (
                <li key={c.user_id} className="flex items-start gap-2 text-xs">
                  <RankIcon className={cn('w-4 h-4 mt-0.5 shrink-0', rankColor)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {userNameById.get(c.user_id) || c.user_id}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 shrink-0">
                        score {c.score ?? 0}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                      {stateLabel}
                    </div>
                    {c.reasons && c.reasons.length > 0 && (
                      <div className="text-[10px] text-gray-600 dark:text-gray-300 mt-0.5">
                        {c.reasons.join(' · ')}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </PopoverContent>
    </Popover>
  );
}
